import { MANAGEMENT_PAGE_SIZE } from '@/constants';
import { useFaviconAlert } from '@/data/hook/useFaviconAlert';
import { useSoundAlert } from '@/data/hook/useSoundAlert';
import { ListAlertsFilters } from '@/data/services/alertServices';
import { useTranslation } from 'react-i18next';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import Text from '@/components/ui/custom/dataDisplay/Text';
import InfinityList from '@/components/ui/custom/InfinityList';
import { LoadingIcon } from '../../icons';
import NotificationFactory from '../item/factory/NotificationFactory';
import useInfiniteService from '@/data/hook/useInfiniteService';
import { alertQueryKeys } from '@/data/services/querykeys';
import useWs from '@/data/hook/useWs';
import { AlertEvent, AlertTypeEnum } from '@/models/Alert';
import SoundAlert from './SoundAlert';

type ListNotifications = {
  filters: ListAlertsFilters;
  showCount?: boolean;
};

export default function NotificationsList({
  filters: filtersProp,
  showCount,
}: ListNotifications) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'alerts.list',
  });

  const filters = {
    ...filtersProp,
    pageSize: MANAGEMENT_PAGE_SIZE,
  };
  const { ref, playSound } = useSoundAlert();

  const { changeFavicon } = useFaviconAlert();
  const {
    results: alerts,
    isInitialLoading,
    fetchNextPage,
    hasNextPage,
    invalidate,
    meta,
  } = useInfiniteService(alertQueryKeys.list(filters)._ctx.infinity);
  useWs<AlertEvent>('alerts', {
    onMessage: async message => {
      await invalidate();
      if (message.data.alert.type !== AlertTypeEnum.LATENESS) return;
      playSound();
      changeFavicon();
    },
  });

  return (
    <div
      className="w-full pt-2 flex flex-col gap-2.5 overflow-hidden"
      data-testid="notificationsList"
    >
      <SoundAlert ref={ref} />
      <ConditionalRenderer condition={showCount && meta?.pagination.count}>
        <Text
          text={t('totalOf', {
            count: meta?.pagination.count,
          })}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={isInitialLoading}>
        <div className="flex justify-center w-full text-primary">
          <LoadingIcon className="w-5 h-5" />
        </div>
      </ConditionalRenderer>
      <ConditionalRenderer condition={alerts.length === 0 && !isInitialLoading}>
        <div className="flex justify-center w-full text-primary">
          <Text text={t('empty')} className="text-base-content" />
        </div>
      </ConditionalRenderer>

      <InfinityList
        className="flex flex-col gap-1.5 h-full pr-2 w-full"
        onReachEnd={fetchNextPage}
        scroll
        hasNextPage={hasNextPage}
      >
        {alerts.map(notification => (
          <NotificationFactory
            key={notification.id}
            filters={filters}
            notification={notification}
          />
        ))}
      </InfinityList>
    </div>
  );
}
